import React, { useEffect, useRef, useContext } from "react";
import { Form, Button } from "react-bootstrap";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import keyBy from "lodash/keyBy";
import omit from "lodash/omit";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import StarekContext from "../../context/StarekContext";
import {
    SidebarView,
    SidebarContext,
    SidebarContextProvider,
    useSyncedField,
    usePageValidator,
} from "../../components/Sidebar";
import CycleStageCard from "./CycleStageCard";

function CycleBasicDetails() {
    const { entity } = useContext(SidebarContext);
    const [cycleName, setCycleName] = useSyncedField("name", "");
    const [cycleDesc, setCycleDesc] = useSyncedField("description", "");

    const entityIdRef = useRef(null);

    // Register validator for this page
    usePageValidator("basic_details", (emittedData) => {
        // Name is required
        return emittedData.name?.trim().length > 0;
    });

    useEffect(() => {
        const currentEntityId = entity?.id || null;
        if (
            entityIdRef.current === currentEntityId &&
            currentEntityId !== null
        ) {
            return;
        }
        entityIdRef.current = currentEntityId;

        if (entity) {
            setCycleName(entity.name || "");
            setCycleDesc(entity.description || "");
        } else {
            setCycleName("");
            setCycleDesc("");
        }
    }, [entity?.id]);

    return (
        <Form
            className="d-flex flex-column h-100"
            onSubmit={(e) => e.preventDefault()}
        >
            <Form.Group className="mb-3">
                <Form.Label>
                    Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter here"
                    value={cycleName}
                    onChange={(e) => setCycleName(e.target.value)}
                    disabled
                    required
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Optional, up to 191 characters"
                    maxLength={191}
                    value={cycleDesc}
                    onChange={(e) => setCycleDesc(e.target.value)}
                    disabled
                />
            </Form.Group>
        </Form>
    );
}

function CycleStages() {
    const { entity } = useContext(SidebarContext);
    const [cycleStageCard, setCycleStageCard] = useSyncedField(
        "cycleStageCard",
        [],
    );
    const [cycleStagesData, setCycleStagesData] = useSyncedField(
        "cycleStagesData",
        {},
    );
    const [deletedStageIds, setDeletedStageIds] = useSyncedField(
        "deletedStageIds",
        [],
    );

    const entityIdRef = useRef(null);

    // Setup drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Register validator for this page
    usePageValidator("cycle_stages", (emittedData) => {
        const cycleStagesData = emittedData.cycleStagesData || {};
        const stages = Object.values(cycleStagesData);

        // Must have at least one valid stage
        const validStages = stages.filter(
            (s) =>
                s &&
                typeof s.name === "string" &&
                s.name.trim().length > 0 &&
                Number.isFinite(parseInt(s.duration)) &&
                parseInt(s.duration) > 0 &&
                (s.is_productive === true || s.is_productive === false),
        );

        return validStages.length >= 1;
    });

    useEffect(() => {
        const currentEntityId = entity?.id || null;
        if (
            entityIdRef.current === currentEntityId &&
            currentEntityId !== null
        ) {
            return;
        }
        entityIdRef.current = currentEntityId;

        const stages = Array.isArray(entity?.cycle_stages)
            ? entity.cycle_stages
            : [];

        if (entity && entity.id && stages.length > 0) {
            setCycleStageCard(
                stages.map((s) => ({ id: s.id, expanded: true })),
            );

            const initial = {};
            stages.forEach((s) => {
                initial[s.id] = {
                    id: s.id,
                    name: s.name || "",
                    duration: Number(s.duration) || 0,
                    is_productive: !!s.is_productive,
                    description: s.description || "",
                };
            });
            setCycleStagesData(initial);
            setDeletedStageIds([]);
        } else {
            setCycleStageCard([{ id: Date.now(), expanded: true }]);
            setCycleStagesData({});
            setDeletedStageIds([]);
        }
    }, [entity?.id]);


    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = cycleStageCard.findIndex(
                (item) => item.id === active.id,
            );
            const newIndex = cycleStageCard.findIndex(
                (item) => item.id === over.id,
            );
            const reorderedItems = arrayMove(
                cycleStageCard,
                oldIndex,
                newIndex,
            );
            setCycleStageCard(reorderedItems);
        }
    };

    const removeCycleStageCard = (id) => {
        if (cycleStageCard.length <= 1) {
            return;
        }
        setCycleStageCard(cycleStageCard.filter((stage) => stage.id !== id));
        setCycleStagesData(omit(cycleStagesData, [id]));

        const wasExisting =
            Array.isArray(entity?.cycle_stages) &&
            entity.cycle_stages.some((s) => s.id === id);
        if (wasExisting) {
            setDeletedStageIds(
                deletedStageIds.includes(id)
                    ? deletedStageIds
                    : [...deletedStageIds, id],
            );
        }
    };

    const updateCycleStage = (id, data) => {
        const isExisting =
            Array.isArray(entity?.cycle_stages) &&
            entity.cycle_stages.some((s) => s.id === id);

        setCycleStagesData({
            ...cycleStagesData,
            [id]: {
                ...(cycleStagesData[id] || {}),
                ...(isExisting ? { id } : {}),
                ...data,
            },
        });
    };

    return (
        <Form className="d-flex flex-column h-100">
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div>Cycle Stages</div>
                <div className="text-muted small">
                    Read-only — cycles are recorded from operation completion now
                </div>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={cycleStageCard.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {cycleStageCard.map((stage, index) => (
                        <SortableCycleStageCard
                            key={stage.id}
                            id={stage.id}
                            index={index}
                            expanded={!!stage.expanded}
                            hideDelete={index === 0}
                            removeCycleStage={removeCycleStageCard}
                            updateCycleStage={updateCycleStage}
                            initialData={cycleStagesData[stage.id]}
                        />
                    ))}
                </SortableContext>
            </DndContext>
        </Form>
    );
}

// Wrapper component for sortable cycle stage card
function SortableCycleStageCard(props) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <CycleStageCard
                {...props}
                position={props.index + 1}
                dragHandleProps={{ ...attributes, ...listeners }}
            />
        </div>
    );
}

const CycleSidebar = (props) => {
    const {
        show,
        onDiscard,
        item = null,
        plantId,
        onSave = async () => {},
        onDelete = async () => {},
        onToggleActive,
    } = props;
    const starekContext = useContext(StarekContext);

    const handleSaveInternal = async (emittedData, entity) => {
        try {
            const cycleName = emittedData.name?.trim();
            if (!cycleName) {
                starekContext.showError("Cycle name is required");
                return; // Don't throw, just return
            }
            if (!plantId) {
                starekContext.showError("Plant ID is required");
                return; // Don't throw, just return
            }

            const cycleStagesData = emittedData.cycleStagesData || {};
            const deletedStageIds = emittedData.deletedStageIds || [];

            // Validation
            const stages = Object.values(cycleStagesData);
            const validStages = stages.filter(
                (s) =>
                    s &&
                    typeof s.name === "string" &&
                    s.name.trim().length > 0 &&
                    Number.isFinite(parseInt(s.duration)) &&
                    parseInt(s.duration) > 0 &&
                    (s.is_productive === true || s.is_productive === false),
            );

            if (validStages.length < 1) {
                starekContext.showError(
                    "Please add at least one cycle stage with Name, Duration and Type.",
                );
                return; // Don't throw, just return
            }

            // Check for duplicate stage names
            const names = validStages
                .map((s) => (s.name || "").trim().toLowerCase())
                .filter((n) => n.length > 0);
            if (new Set(names).size !== names.length) {
                starekContext.showError(
                    "Cycle stages can't have the same name",
                );
                return; // Don't throw, just return
            }

            // Check for zero duration
            if (
                validStages.some(
                    (s) =>
                        Number.isFinite(parseInt(s.duration)) &&
                        parseInt(s.duration) === 0,
                )
            ) {
                starekContext.showError(
                    "Cycle stages must have a duration greater than zero.",
                );
                return; // Don't throw, just return
            }

            const normalizeStage = (s = {}) => ({
                id: s.id ?? undefined,
                name: (s.name || "").trim(),
                duration: Number.parseInt(s.duration) || 0,
                is_productive: s.is_productive === true,
                description: (s.description || "").trim(),
            });

            if (entity?.id) {
                // Update
                const originals = Array.isArray(entity?.cycle_stages)
                    ? entity.cycle_stages
                    : [];
                const originalById = keyBy(originals.map(normalizeStage), "id");
                const currentStages = validStages.map(normalizeStage);

                const currentIds = new Set(
                    validStages.map((s) => s?.id).filter(Boolean),
                );
                const originalIds = new Set(originals.map((s) => s.id));

                const computedToDelete = [...originalIds].filter(
                    (oid) => !currentIds.has(oid),
                );
                const toDelete = Array.from(
                    new Set([...computedToDelete, ...deletedStageIds]),
                );

                const originalSignatures = new Set(
                    originals.map((os) =>
                        JSON.stringify({
                            ...normalizeStage(os),
                            id: undefined,
                        }),
                    ),
                );

                const toCreate = currentStages
                    .filter((s) => !s.id || !originalById[s.id])
                    .map((s) => ({ ...s, id: undefined }))
                    .filter(
                        (s) =>
                            !originalSignatures.has(
                                JSON.stringify({ ...s, id: undefined }),
                            ),
                    );

                const toUpdate = currentStages.filter((s) => {
                    if (!s.id || !originalById[s.id]) return false;
                    const prev = originalById[s.id];
                    return !isEqual(
                        { ...normalizeStage(prev), id: undefined },
                        { ...s, id: undefined },
                    );
                });

                const payload = {
                    id: entity.id,
                    name: cycleName,
                    description: emittedData.description || "",
                    deletedStageIds: toDelete,
                    stagesToUpdate: toUpdate,
                    stagesToCreate: toCreate,
                };

                await onSave(payload);
            } else {
                // Create
                const payload = {
                    name: cycleName,
                    description: emittedData.description || "",
                    stages: validStages.map(normalizeStage),
                };

                await onSave(payload);
            }

            starekContext.showSuccess(
                entity?.id
                    ? `Cycle "${cycleName}" updated successfully`
                    : `Cycle "${cycleName}" created successfully`,
            );
        } catch (error) {
            console.error("Error saving cycle:", error);
            // Show error using StarekContext
            starekContext.showError(
                error?.message || "Failed to save cycle. Please try again.",
            );
            // Don't re-throw - the error has been handled
        }
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={onDelete}
            onDiscard={onDiscard}
            onToggleActive={onToggleActive}
            initialPage="basic_details"
        >
            <SidebarView entityName="Cycle" readOnly>
                <SidebarView.Page name="basic_details" title="Basic Details">
                    <CycleBasicDetails />
                </SidebarView.Page>

                <SidebarView.Page name="cycle_stages" title="Cycle Stages">
                    <CycleStages />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
};

export default CycleSidebar;
